import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { resolveColor } from '../../theme/colorTokens.js';
import { marginFields, visibilityField, weightField } from '../_shared/blockFields.js';
import { marginStyle, num, readWeight, str, visibilityCss } from '../_shared/blockRender.js';
/**
 * Shrine `button` ("Link button") — config-by-config. A standalone link/button content block.
 *
 * Shrine settings reproduced (same ids / defaults / order / Shrine header groups):
 *  - General: `visibility` (Display on), `button_label`, `button_link`.
 *  - Styling: `button_style_secondary` (outline) + `large_font_size` (larger font) + custom colours
 *    (`enable_custom_color` → `custom_color` / `custom_text_color` / `custom_outline_hover_text_color`).
 *  - Layout: `full_width`, `alignment` (desktop) + `mobile_alignment` (responsive at Shrine's 750px).
 *  - Icons: `icon_scale` (% glyph size), `icon_spacing` (px gap), `prefix_icon` / `suffix_icon`
 *    (leading/trailing uploaded glyph images).
 *  - Margins: `margin_top` / `margin_bottom` (Shrine default 15 / 15).
 *
 * Button look is reused from `buy-buttons` (.button / .button--secondary): square-ish, 6px radius var,
 * weight 700, min-height ~47px. The TASK additionally asks for a third "link" style (a borderless text
 * link), so `button_style_secondary` (a Shrine checkbox) is promoted to a 3-value `button_style` select
 * (Primary / Outline / Link) — Primary + Outline map 1:1 onto Shrine's checkbox states (false / true),
 * Link is the added variant.
 *
 * ICONS — Shrine's `prefix_icon` / `suffix_icon` are Shopify `image_picker` glyph uploads sized via an
 * `image_url` CDN filter and scaled by `icon_scale`, gap `icon_spacing`. We reproduce them with our own
 * uploaded-image (`type:'image'`) fields (no glyph FONT, no CDN srcset — see DEFERRED), rendering the
 * uploaded image as an inline glyph whose height scales with `icon_scale` and whose gap to the label is
 * `icon_spacing`. An empty icon field simply renders nothing (Shrine `if block.settings.prefix_icon`).
 *
 * DEFERRED (Shopify-platform only — noted, not reproduced):
 *  - Shopify CDN `image_url`/srcset responsive sizing for the icon assets — we use the uploaded asset
 *    URL as-is (same policy as the `image` block).
 *  - `custom_outline_hover_text_color` depends on the Shopify theme-global `settings.link_btn_hover`
 *    fill-direction animation; we keep + apply the colour as the outline hover text colour, but the
 *    theme-global hover-fill sweep animation itself is a global theme-settings construct, not reproduced.
 *  - Reveal-on-scroll animation — not part of this block.
 */
export const buttonBlockSchema = {
    type: 'button',
    name: 'Link button',
    fields: [
        // ── General ──────────────────────────────────────────────────────────────
        visibilityField,
        {
            id: 'button_label',
            type: 'text',
            label: 'Button label',
            default: 'Découvrir',
            defaultKey: 'hero.shop_now',
            maxLength: 120,
        },
        {
            id: 'button_link',
            type: 'link',
            label: 'Button link',
        },
        // ── Styling ──────────────────────────────────────────────────────────────
        { ...weightField, group: 'Styling' },
        {
            id: 'button_style',
            type: 'select',
            label: 'Button style',
            group: 'Styling',
            default: 'primary',
            options: [
                { value: 'primary', label: 'Primary' },
                { value: 'secondary', label: 'Outline' },
                { value: 'link', label: 'Link' },
            ],
        },
        {
            id: 'large_font_size',
            type: 'checkbox',
            label: 'Use larger font size',
            group: 'Styling',
            default: false,
        },
        {
            id: 'enable_custom_color',
            type: 'checkbox',
            label: 'Enable custom button colors',
            group: 'Styling',
            info: 'The color selections below are applied if this setting is enabled.',
            default: false,
        },
        {
            id: 'custom_color',
            type: 'color',
            label: 'Button custom color',
            group: 'Styling',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'enable_custom_color', equals: true },
        },
        {
            id: 'custom_text_color',
            type: 'color',
            label: 'Button custom text color',
            group: 'Styling',
            default: { ref: 'custom', value: '#ffffff' },
            // Shrine: shown only when custom colour on AND NOT outline (outline has no fill text).
            visibleWhen: { field: 'enable_custom_color', equals: true },
        },
        {
            id: 'custom_outline_hover_text_color',
            type: 'color',
            label: 'Button custom hover text color',
            group: 'Styling',
            info: 'Applied to the outline style on hover.',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'enable_custom_color', equals: true },
        },
        // ── Layout ───────────────────────────────────────────────────────────────
        {
            id: 'full_width',
            type: 'checkbox',
            label: 'Full button width',
            group: 'Layout',
            default: false,
        },
        {
            id: 'alignment',
            type: 'select',
            label: 'Desktop alignment',
            group: 'Layout',
            default: 'flex-start',
            options: [
                { value: 'flex-start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Right' },
            ],
        },
        {
            id: 'mobile_alignment',
            type: 'select',
            label: 'Mobile alignment',
            group: 'Layout',
            default: 'center',
            options: [
                { value: 'flex-start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Right' },
            ],
        },
        // ── Icons ────────────────────────────────────────────────────────────────
        {
            id: 'icon_scale',
            type: 'range',
            label: 'Icon size',
            group: 'Icons',
            min: 80,
            max: 180,
            step: 5,
            default: 120,
            unit: '%',
        },
        {
            id: 'icon_spacing',
            type: 'range',
            label: 'Icon spacing',
            group: 'Icons',
            min: 0,
            max: 40,
            step: 2,
            default: 10,
            unit: 'px',
        },
        {
            id: 'prefix_icon',
            type: 'image',
            label: 'Prefix icon',
            group: 'Icons',
        },
        {
            id: 'suffix_icon',
            type: 'image',
            label: 'Suffix icon',
            group: 'Icons',
        },
        // ── Margins (Shrine default 15 / 15) ─────────────────────────────────────
        ...marginFields(15, 15),
    ],
};
/** Read the tagged LinkValue's label (Shrine has no separate label — the button_label always wins). */
function linkOf(v) {
    if (v && typeof v === 'object' && 'type' in v) {
        return v;
    }
    return null;
}
/** Read an uploaded icon's ImageValue when a real image is set (non-empty url). */
function iconOf(v) {
    if (v && typeof v === 'object' && typeof v.url === 'string') {
        const iv = v;
        if (iv.url.trim() !== '')
            return iv;
    }
    return null;
}
/**
 * Standalone link button. Renders an `<a>` when a link target is set (resolved via ctx.resolveUrl so a
 * slug change never breaks it), else a disabled `<button>` (Shrine renders `role="link" aria-disabled`).
 * Desktop alignment is the base; mobile alignment overrides at Shrine's 750px via BOTH `@media` (live
 * viewport) and `@container aqx-page` (builder device-preview), mirroring the title block. A prefix
 * and/or suffix icon (uploaded image) flanks the label, sized by `icon_scale` (% of the line) and gapped
 * by `icon_spacing` (px).
 */
const ButtonBlock = ({ block, context }) => {
    const bs = block.settings;
    const cls = `aqx-bt-${block.id}`;
    const label = str(bs.button_label) || 'Button label';
    const weight = readWeight(bs); // shared "Font weight" override ('' → keep existing weight)
    const style = str(bs.button_style, 'primary'); // primary | secondary | link
    const isOutline = style === 'secondary';
    const isLink = style === 'link';
    const large = bs.large_font_size === true;
    const fullWidth = bs.full_width === true;
    const deskAlign = str(bs.alignment, 'flex-start');
    const mobAlign = str(bs.mobile_alignment, 'center');
    // ── Icons (Shrine prefix_icon / suffix_icon + icon_scale / icon_spacing). ──
    const prefixIcon = iconOf(bs.prefix_icon);
    const suffixIcon = iconOf(bs.suffix_icon);
    const iconScale = num(bs.icon_scale, 120); // % of the base font size
    const iconSpacing = num(bs.icon_spacing, 10); // px gap label↔icon
    // Glyph height tracks the label line: base ~1em (15/18px) scaled by icon_scale%.
    const iconSize = `calc(${large ? 18 : 15}px * ${iconScale / 100})`;
    const iconStyle = {
        display: 'inline-block',
        blockSize: iconSize,
        inlineSize: 'auto',
        objectFit: 'contain',
        flex: '0 0 auto',
    };
    // ── Custom colours (Shrine enable_custom_color → custom_color / custom_text_color). ──
    const customOn = bs.enable_custom_color === true;
    const customFill = customOn
        ? (resolveColor(bs.custom_color) ?? 'var(--aqx-scheme-accent)')
        : 'var(--aqx-btn-bg, var(--aqx-scheme-accent))';
    const customText = customOn
        ? (resolveColor(bs.custom_text_color) ?? 'var(--aqx-theme-accent-contrast, #fff)')
        : 'var(--aqx-btn-text, var(--aqx-theme-accent-contrast, #fff))';
    const customHoverText = customOn && isOutline
        ? (resolveColor(bs.custom_outline_hover_text_color) ?? null)
        : null;
    // ── Base button look (reused from buy-buttons: weight 700, 6px radius var, ≥47px). ──
    const base = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: `${iconSpacing}px`,
        minBlockSize: isLink ? undefined : '47px',
        border: '1px solid transparent',
        cursor: 'pointer',
        textDecoration: 'none',
        fontFamily: 'inherit',
        fontWeight: weight || 'var(--aqx-theme-fw-bold, 700)',
        lineHeight: 1.2,
        fontSize: large ? '18px' : '15px',
        inlineSize: fullWidth && !isLink ? '100%' : undefined,
    };
    let look;
    if (isLink) {
        // Borderless text link — accent text, underline (the added "link" style).
        look = {
            ...base,
            background: 'transparent',
            color: customOn ? customFill : 'var(--aqx-scheme-accent)',
            border: 'none',
            paddingBlock: 0,
            paddingInline: 0,
            minBlockSize: undefined,
            textDecoration: 'underline',
            textUnderlineOffset: '0.2em',
        };
    }
    else if (isOutline) {
        // Shrine .button--secondary: transparent fill, foreground text + 1px foreground outline. The non-custom
        // default reads a section-overridable var (Shrine's `--color-base-outline-button-labels`) so a wrapping
        // section's custom color scheme can retint nested outline buttons; falls back to the scheme text colour.
        const outlineColor = customOn ? customFill : 'var(--aqx-btn-outline, var(--aqx-scheme-text))';
        look = {
            ...base,
            background: 'transparent',
            color: outlineColor,
            border: `1px solid ${outlineColor}`,
            paddingBlock: '8px',
            paddingInline: '30px',
            borderRadius: 'var(--aqx-theme-radius-button, 6px)',
        };
    }
    else {
        // Shrine .button--primary: filled accent.
        look = {
            ...base,
            background: customFill,
            color: customText,
            paddingBlock: '8px',
            paddingInline: '30px',
            borderRadius: 'var(--aqx-theme-radius-button, 6px)',
        };
    }
    // ── Responsive alignment (Shrine 750px). Desktop = base; mobile overrides. ──
    // The button sits in a flex row whose justify-content is the alignment; mobile overrides justify only.
    const wrapBase = `.${cls}{display:flex;justify-content:${deskAlign}}`;
    const mobileRule = `.${cls}{justify-content:${mobAlign}}`;
    let css = wrapBase +
        `@media(max-width:749px){${mobileRule}}` +
        `@container aqx-page (max-width:749px){${mobileRule}}` +
        visibilityCss(cls, bs.visibility);
    // Outline custom hover text colour (deferred theme-global animation; colour swap kept).
    if (customHoverText) {
        css += `.${cls} > a:hover,.${cls} > button:hover{color:${customHoverText}!important}`;
    }
    // ── Link target: <a> when set (resolved URL), else disabled <button>. ──
    const link = linkOf(bs.button_link);
    const href = link ? context.resolveUrl(link) : '';
    const newTab = link && 'newTab' in link ? link.newTab === true : false;
    const inner = (_jsxs(_Fragment, { children: [prefixIcon ? (_jsx("img", { src: prefixIcon.url, alt: str(prefixIcon.alt), style: iconStyle, "aria-hidden": prefixIcon.alt ? undefined : 'true' })) : null, _jsx("span", { children: label }), suffixIcon ? (_jsx("img", { src: suffixIcon.url, alt: str(suffixIcon.alt), style: iconStyle, "aria-hidden": suffixIcon.alt ? undefined : 'true' })) : null] }));
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx("div", { className: cls, style: marginStyle(bs, 15, 15), children: href ? (_jsx("a", { href: href, style: look, ...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}), children: inner })) : (_jsx("button", { type: "button", role: "link", "aria-disabled": "true", style: look, children: inner })) })] }));
};
export const buttonBlock = {
    type: 'button',
    schema: buttonBlockSchema,
    Component: ButtonBlock,
    meta: { label: 'Link button' },
};
//# sourceMappingURL=index.js.map